/**
 * Pannello confronto preventivi per una richiesta di preventivo (RDP).
 * Tabella pivot: righe = articoli, colonne = fornitori, celle = prezzo.
 * Evidenzia il prezzo piu basso per ogni riga in verde.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';

interface ConfrontoPreventiviPanelProps {
  richiestaId: string;
}

interface ConfrontoRiga {
  codice_articolo: string | null;
  descrizione: string;
  quantita: string;
  preventivi: Record<string, string | null>;
}

interface ConfrontoResponse {
  richiesta_id: string;
  fornitori: string[];
  righe: ConfrontoRiga[];
}

interface Fornitore {
  id: string;
  ragione_sociale: string | null;
  nome: string | null;
  cognome: string | null;
}

export function ConfrontoPreventiviPanel({ richiestaId }: ConfrontoPreventiviPanelProps) {
  const { currency, qty } = useFormatters();
  const token = localStorage.getItem('token');

  const [confronto, setConfronto] = useState<ConfrontoResponse | null>(null);
  const [fornitoriMap, setFornitoriMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const fetchConfronto = useCallback(async () => {
    setLoading(true);
    const [confRes, fornRes] = await Promise.all([
      fetch(`/api/v1/richieste/${richiestaId}/confronto`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch('/api/v1/soggetti?tipo=fornitore&limit=200', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (confRes.ok) setConfronto(await confRes.json());
    if (fornRes.ok) {
      const data = await fornRes.json();
      const items: Fornitore[] = Array.isArray(data) ? data : data.items ?? [];
      const map: Record<string, string> = {};
      for (const f of items) {
        map[f.id] = f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || f.id.substring(0, 8);
      }
      setFornitoriMap(map);
    }
    setLoading(false);
  }, [richiestaId, token]);

  useEffect(() => {
    fetchConfronto();
  }, [fetchConfronto]);

  if (loading) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">Caricamento confronto...</p>
      </Card>
    );
  }

  if (!confronto || confronto.righe.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">Nessun dato per il confronto</p>
      </Card>
    );
  }

  /**
   * Trova il prezzo minimo tra i fornitori per una riga.
   * Restituisce null se nessun prezzo disponibile.
   */
  const findMinPrice = (riga: ConfrontoRiga): number | null => {
    let min: number | null = null;
    for (const fornitoreId of confronto.fornitori) {
      const val = riga.preventivi[fornitoreId];
      if (val != null) {
        const num = parseFloat(val);
        if (!isNaN(num) && (min === null || num < min)) {
          min = num;
        }
      }
    }
    return min;
  };

  return (
    <Card className="overflow-hidden">
      <div className="px-4 py-2 border-b border-border bg-muted/50">
        <h2 className="text-sm font-semibold text-foreground">Confronto preventivi</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-2 text-muted-foreground font-medium">Codice</th>
              <th className="text-left px-4 py-2 text-muted-foreground font-medium">Descrizione</th>
              <th className="text-right px-4 py-2 text-muted-foreground font-medium">Q.ta</th>
              {confronto.fornitori.map((fId) => (
                <th
                  key={fId}
                  className="text-right px-4 py-2 text-muted-foreground font-medium"
                  title={fId}
                >
                  {fornitoriMap[fId] || fId.substring(0, 8)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {confronto.righe.map((riga, idx) => {
              const minPrice = findMinPrice(riga);
              return (
                <tr key={idx} className="border-b border-border">
                  <td className="px-4 py-2 font-mono text-xs">
                    {riga.codice_articolo || '—'}
                  </td>
                  <td className="px-4 py-2">{riga.descrizione}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {qty(riga.quantita)}
                  </td>
                  {confronto.fornitori.map((fId) => {
                    const val = riga.preventivi[fId];
                    if (val == null) {
                      return (
                        <td key={fId} className="px-4 py-2 text-right text-muted-foreground">
                          —
                        </td>
                      );
                    }
                    const num = parseFloat(val);
                    const isMin = minPrice !== null && !isNaN(num) && num === minPrice;
                    return (
                      <td
                        key={fId}
                        className={`px-4 py-2 text-right tabular-nums ${
                          isMin
                            ? 'text-green-700 dark:text-green-400 font-semibold'
                            : ''
                        }`}
                      >
                        {currency(val)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
